/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaEvidenceUrls.ts
 * FILE_ID: JOBHUB-QA-EVIDENCE-URLS-001
 * PURPOSE: Load signed evidence URLs without refetching area executions.
 */
import { createElement, useContext, type ReactNode } from 'react'
import { QueryClientContext, useQueries, type QueryClient } from '@tanstack/react-query'
import type { QaApi, QaAreaExecution, QaEvidence } from '../../qa/qaApi'

export type QaEvidenceUrlMap = Record<string, {
  asset_url: string | null
  display_url: string | null
  photo_url: string | null
}>

const STALE_MS = 10 * 60 * 1000

function fillEvidenceUrls(row: QaEvidence, urls: QaEvidenceUrlMap[string] | undefined): QaEvidence {
  if (!urls) return row
  return {
    ...row,
    asset_url: row.asset_url ?? urls.asset_url,
    display_url: row.display_url ?? urls.display_url,
    photo_url: row.photo_url ?? urls.photo_url,
  }
}

export function mergeEvidenceUrls(
  area: QaAreaExecution,
  urls: QaEvidenceUrlMap | undefined,
): QaAreaExecution {
  if (!urls) return area
  return {
    ...area,
    evidence: (area.evidence ?? []).map((row) => fillEvidenceUrls(row, urls[row.id])),
  }
}

export function useQaEvidenceUrls(
  api: Pick<QaApi, 'getEvidenceUrls'>,
  areaIds: string[],
  enabled: boolean,
) {
  return useQueries({
    queries: areaIds.map((areaId) => ({
      queryKey: ['qa-evidence-urls', areaId] as const,
      queryFn: () => api.getEvidenceUrls(areaId),
      enabled: enabled && Boolean(areaId),
      staleTime: STALE_MS,
    })),
  })
}

export function rememberEvidencePhoto(
  queryClient: QueryClient,
  areaId: string,
  evidence: { id: string; photo_url?: string | null },
): void {
  const photoUrl = evidence.photo_url?.trim() ?? ''
  if (photoUrl) {
    queryClient.setQueryData<QaEvidenceUrlMap>(['qa-evidence-urls', areaId], (current) => {
      const existing = current?.[evidence.id]
      if (existing?.photo_url) return current
      return {
        ...(current ?? {}),
        [evidence.id]: { asset_url: null, display_url: null, photo_url: photoUrl },
      }
    })
  }
  void queryClient.invalidateQueries({ queryKey: ['qa-evidence-urls', areaId] })
}

export function forgetEvidencePhoto(
  queryClient: QueryClient,
  areaId: string,
  evidenceId: string,
): void {
  queryClient.setQueryData<QaEvidenceUrlMap>(['qa-evidence-urls', areaId], (current) => {
    if (!current || !(evidenceId in current)) return current
    const next = { ...current }
    delete next[evidenceId]
    return next
  })
  void queryClient.invalidateQueries({ queryKey: ['qa-evidence-urls', areaId] })
}

export async function areasWithEvidenceUrls(
  queryClient: QueryClient,
  api: Partial<Pick<QaApi, 'getEvidenceUrls'>>,
  areas: QaAreaExecution[],
): Promise<QaAreaExecution[]> {
  if (typeof api.getEvidenceUrls !== 'function' || areas.length === 0) return areas
  const load = api.getEvidenceUrls
  const maps = await Promise.all(areas.map(async (area) => {
    const urls = await queryClient.fetchQuery({
      queryKey: ['qa-evidence-urls', area.id],
      queryFn: () => load(area.id),
      staleTime: STALE_MS,
    })
    return [area.id, urls] as const
  }))
  const byId = Object.fromEntries(maps)
  return areas.map((area) => mergeEvidenceUrls(area, byId[area.id]))
}

function QaMergedAreasQuery({
  api,
  areas,
  children,
}: {
  api: Pick<QaApi, 'getEvidenceUrls'>
  areas: QaAreaExecution[]
  children: (areas: QaAreaExecution[]) => ReactNode
}) {
  const results = useQaEvidenceUrls(api, areas.map((area) => area.id), true)
  const merged = areas.map((area, index) => mergeEvidenceUrls(area, results[index]?.data))
  return children(merged)
}

/** Run and ITP only. Skips the request when there is no query client (tests) or on Build. */
export function QaMergedAreas({
  api,
  areas,
  enabled,
  children,
}: {
  api: Partial<Pick<QaApi, 'getEvidenceUrls'>>
  areas: QaAreaExecution[]
  enabled: boolean
  children: (areas: QaAreaExecution[]) => ReactNode
}) {
  const client = useContext(QueryClientContext)
  if (!enabled || !client || typeof api.getEvidenceUrls !== 'function') return children(areas)
  return createElement(QaMergedAreasQuery, {
    api: api as Pick<QaApi, 'getEvidenceUrls'>,
    areas,
    children,
  })
}
